import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';

import Tabs from 'components/common/tabs';

import { useNavigate, useLocation } from 'react-router';

type BaseMenuProps = {
  setActiveMenu: React.Dispatch<string>;
  activeMenu: string;
  menus: readonly string[];
  children: React.ReactNode;
  renderMenuItem?: (key: string) => React.ReactNode;
};

/**
 * `compact` is the former menu2: no url sync, no scroll anchor, no custom actions, and the bar sits
 * straight in the container instead of the page variant's ruled row.
 *
 * A union rather than one optional bag: the compact branch renders no `customActions`, so accepting
 * the prop there would swallow it silently — `never` turns that into a compile error.
 */
export type MenuProps = BaseMenuProps &
  ({ variant?: 'page'; customActions?: React.ReactNode } | { variant: 'compact'; customActions?: never });

export const TAB_SEARCH_PARAM_NAME = 'tabname';
export const TAB_ANCHOR_HASH = '#tab';

/** The page tab bar, rendered as Nav / Tab through `components/common/tabs`. */
const TabsMenu: React.FunctionComponent<MenuProps> = ({
  activeMenu,
  setActiveMenu,
  menus,
  children,
  renderMenuItem,
  customActions,
  variant = 'page'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const syncsWithUrl = variant === 'page';

  useEffect(() => {
    if (!syncsWithUrl) return;

    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(TAB_SEARCH_PARAM_NAME)) {
      const tab = searchParams.get(TAB_SEARCH_PARAM_NAME);

      if (tab && menus.includes(tab)) {
        setActiveMenu(tab);
      }
    }
  }, [location, setActiveMenu, menus, syncsWithUrl]);

  const onTabChange = (key: string) => {
    if (syncsWithUrl) {
      const searchParams = new URLSearchParams(location.search);

      searchParams.set(TAB_SEARCH_PARAM_NAME, key);
      navigate({ pathname: location.pathname, search: searchParams.toString(), hash: location.hash });
    }

    setActiveMenu(key);
  };

  const renderMenuItemFunc = renderMenuItem ?? ((menu: string) => menu);

  const tabsProps = {
    activeKey: activeMenu,
    onChange: onTabChange,
    items: menus.map(menuKey => ({
      key: menuKey,
      // A div, not a span: ::first-letter sentence-casing needs a block container. `menu-<key>` is the
      // e2e hook cypress clicks.
      label: <div data-e2e={`menu-${menuKey}`}>{renderMenuItemFunc(menuKey)}</div>
    }))
  };

  const bar = syncsWithUrl ? <PageBarTabs {...tabsProps} /> : <CompactBarTabs {...tabsProps} />;

  return (
    <Container data-e2e={syncsWithUrl ? undefined : 'tabs-menu'}>
      {syncsWithUrl ? (
        <>
          <ScrollIntoViewIfNeeded
            active={location.hash === TAB_ANCHOR_HASH}
            options={{ behavior: 'smooth', block: 'start', scrollMode: 'always' }}
          />
          <MenuContainer>
            {bar}
            {customActions}
          </MenuContainer>
        </>
      ) : (
        // No wrapper on purpose: in menu2 the bar sat directly inside this block container,
        // where its `flex: 1` is inert. A flex wrapper would activate it and move the layout.
        bar
      )}
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const MenuContainer = styled.div`
  display: flex;
  /* The 32px row centres on the bottom rule. */
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => css`
    border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);
    height: 32px;
  `}
`;

const ContentContainer = styled.div`
  /* Figma's Main content container sits 12px under the tab bar (267:6868, gap-12). */
  padding: var(--spacing-md) 0;
`;

/* The bar renders no panes — TabsMenu owns the content slot — but antd mounts a pane holder even for
   childless items, and an empty holder still takes part in layout. */
const barOnly = css`
  min-width: 0;

  .ant-tabs-content-holder {
    display: none;
  }
`;

const PageBarTabs = styled(Tabs)`
  ${barOnly}
  flex: 1;

  /* MenuContainer owns the bar rule for the page variant, so it runs under customActions too;
     a second rule on the nav would double it. */
  .ant-tabs-nav::before {
    border-bottom: none;
  }
`;

const CompactBarTabs = styled(Tabs)`
  ${barOnly}
`;

export default TabsMenu;
